<script setup lang="ts">
import { BaseHeader, type BaseHeaderImage } from '@components/base';
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    headingHtml?: string;
    /**
     * Inline title chips (BaseHeader Super Table). Same order as {{img1}}/{{image1}} in `headingHtml`.
     * From Twig may be a JSON string.
     */
    headingObjects?: BaseHeaderImage[] | string;
    /** Plain text / multiline from Neo */
    description?: string;
    imageUrl?: string;
    imageSrcset?: string;
    imageAlt?: string;
    imageWidth?: number | null;
    imageHeight?: number | null;
  }>(),
  {
    headingHtml: '',
    headingObjects: () => [],
    description: '',
    imageUrl: '',
    imageSrcset: '',
    imageAlt: '',
    imageWidth: null,
    imageHeight: null,
  },
);

function parseHeadingObjects(raw: BaseHeaderImage[] | string | undefined): BaseHeaderImage[] {
  if (raw === undefined || raw === null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    let s = raw.trim();
    if (s === '' || s === '[]') return [];
    try {
      const parsed = JSON.parse(s) as unknown;
      return Array.isArray(parsed) ? (parsed as BaseHeaderImage[]) : [];
    } catch {
      if (s.includes('&quot;')) {
        s = s.replace(/&quot;/g, '"');
        try {
          const parsed = JSON.parse(s) as unknown;
          return Array.isArray(parsed) ? (parsed as BaseHeaderImage[]) : [];
        } catch {
          /* ignore */
        }
      }
      return [];
    }
  }
  return [];
}

const headingObjectsList = computed((): BaseHeaderImage[] => parseHeadingObjects(props.headingObjects));

const show = computed(() => {
  const raw = props.headingHtml ?? '';
  const rawTrim = raw.trim();
  const headingText = raw.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
  const desc = props.description?.trim() ?? '';
  const img = props.imageUrl?.trim() ?? '';
  return rawTrim.length > 0 || headingText.length > 0 || desc.length > 0 || img.length > 0;
});
</script>

<template>
  <section
    v-if="show"
    class="vision-section py-64 font-inter md:py-80 lg:py-64"
  >
    <div class="container">
      <div
        class="flex w-full flex-col-reverse items-stretch gap-40 lg:flex-row lg:items-center lg:gap-x-48 lg:gap-y-0 xl:gap-x-64"
      >
        <div
          class="flex w-full min-w-0 max-w-[560px] flex-1 flex-col lg:max-w-none lg:pr-8 xl:pr-16"
        >
          <BaseHeader
            v-if="headingHtml"
            as="div"
            :content="headingHtml"
            :objects="headingObjectsList"
            content-class="leading-115 md:text-36 font-inter-tight text-28 font-semibold text-off-black-500 sm:text-30 lg:text-40 lg:leading-110 xl:text-42 [&_p]:m-0 [&_p]:leading-[inherit]"
          />
          <p
            v-if="description?.trim()"
            class="mt-24 max-w-[52ch] whitespace-pre-line text-[15.5px] leading-150 text-off-black-500 md:mt-32 md:leading-160 lg:max-w-[48ch] xl:max-w-[52ch]"
          >
            {{ description.trim() }}
          </p>
        </div>

        <div
          class="relative w-full min-w-0 flex-1 shrink-0 lg:max-w-[540px] lg:flex-none lg:pl-8 xl:pl-16"
        >
          <div
            v-if="imageUrl"
            class="relative"
          >
            <img
              :src="imageUrl"
              :srcset="imageSrcset || undefined"
              :alt="imageAlt || ''"
              :width="imageWidth ?? undefined"
              :height="imageHeight ?? undefined"
              class="w-full rounded-[22px] shadow-sm lg:h-[620px] lg:w-[540px] lg:object-cover"
              loading="lazy"
              decoding="async"
            >
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
